/**
 * Geofence helpers (FR-ATT-2, FR-ATT-4, ADR-056, ADR-080), shared by the API (authoritative, at
 * the punch) and the PWA (a hint before sending). Pure functions, no I/O.
 */
/** Mean Earth radius in metres (IUGG). */
const EARTH_RADIUS_M = 6_371_008.8;
/** Great-circle distance in metres (haversine). */
export function distanceMeters(a, b) {
    const rad = (deg) => (deg * Math.PI) / 180;
    const dLat = rad(b.latitude - a.latitude);
    const dLng = rad(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * Whether a fix is inside the circle, giving it the benefit of its reported accuracy (ADR-080,
 * owner): inside when distance − accuracy ≤ radius, with the accuracy credit capped at the
 * radius — a fuzzy fix next to the office passes, a ±2 km fix from across town does not.
 */
export function insideGeofence(fix, circle) {
    const credit = Math.min(Math.max(fix.accuracyM ?? 0, 0), circle.radiusM);
    return distanceMeters(fix, circle) - credit <= circle.radiusM;
}
function parseIpv4(text) {
    const parts = text.split('.');
    if (parts.length !== 4)
        return null;
    let value = 0n;
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part) || (part.length > 1 && part.startsWith('0')))
            return null;
        const n = Number(part);
        if (n > 255)
            return null;
        value = (value << 8n) | BigInt(n);
    }
    return value;
}
function parseIpv6(text) {
    let address = text;
    // An embedded IPv4 tail (e.g. ::ffff:203.0.113.5) is two 16-bit groups.
    const lastColon = address.lastIndexOf(':');
    if (address.slice(lastColon + 1).includes('.')) {
        const v4 = parseIpv4(address.slice(lastColon + 1));
        if (v4 === null)
            return null;
        address = `${address.slice(0, lastColon + 1)}${(v4 >> 16n).toString(16)}:${(v4 & 0xffffn).toString(16)}`;
    }
    const halves = address.split('::');
    if (halves.length > 2)
        return null;
    const groups = (s) => (s === '' ? [] : s.split(':'));
    const head = groups(halves[0] ?? '');
    const tail = halves.length === 2 ? groups(halves[1] ?? '') : [];
    const missing = 8 - head.length - tail.length;
    if (halves.length === 2 ? missing < 1 : missing !== 0)
        return null;
    const all = [...head, ...Array(halves.length === 2 ? missing : 0).fill('0'), ...tail];
    let value = 0n;
    for (const group of all) {
        if (!/^[0-9a-f]{1,4}$/i.test(group))
            return null;
        value = (value << 16n) | BigInt(Number.parseInt(group, 16));
    }
    return value;
}
/** An IPv4 or IPv6 address; IPv4-mapped IPv6 (`::ffff:a.b.c.d`) is read as IPv4. */
export function parseIp(text) {
    const t = text.trim();
    if (t.includes(':')) {
        const v6 = parseIpv6(t);
        if (v6 === null)
            return null;
        if (v6 >> 32n === 0xffffn)
            return { version: 4, value: v6 & 0xffffffffn };
        return { version: 6, value: v6 };
    }
    const v4 = parseIpv4(t);
    return v4 === null ? null : { version: 4, value: v4 };
}
const bitsOf = (version) => (version === 4 ? 32 : 128);
function formatIp({ version, value }) {
    if (version === 4) {
        return [24n, 16n, 8n, 0n].map((s) => String((value >> s) & 0xffn)).join('.');
    }
    const groups = Array.from({ length: 8 }, (_, i) => ((value >> BigInt((7 - i) * 16)) & 0xffffn).toString(16));
    // Compress the longest run of two or more zero groups (RFC 5952).
    let best = { start: -1, length: 0 };
    for (let i = 0; i < 8;) {
        if (groups[i] !== '0') {
            i++;
            continue;
        }
        let j = i;
        while (j < 8 && groups[j] === '0')
            j++;
        if (j - i > best.length && j - i >= 2)
            best = { start: i, length: j - i };
        i = j;
    }
    if (best.start < 0)
        return groups.join(':');
    const head = groups.slice(0, best.start).join(':');
    const tail = groups.slice(best.start + best.length).join(':');
    return `${head}::${tail}`;
}
/**
 * A CIDR range (`203.0.113.0/24`, `2001:db8::/32`) or a single address (→ /32 or /128). Host
 * bits are cleared, so `203.0.113.7/24` is `203.0.113.0/24` (what PostgreSQL `cidr` stores).
 */
export function parseCidr(text) {
    const [address = '', prefixText, extra] = text.trim().split('/');
    if (extra !== undefined)
        return null;
    const ip = parseIp(address);
    if (!ip)
        return null;
    const bits = bitsOf(ip.version);
    if (prefixText !== undefined && !/^\d{1,3}$/.test(prefixText))
        return null;
    const prefix = prefixText === undefined ? bits : Number(prefixText);
    if (prefix > bits)
        return null;
    const mask = prefix === 0 ? 0n : ((1n << BigInt(prefix)) - 1n) << BigInt(bits - prefix);
    return { version: ip.version, network: ip.value & mask, prefix };
}
/** The canonical text of a range, e.g. `203.0.113.0/24`; null when it is not a range. */
export function normalizeCidr(text) {
    const c = parseCidr(text);
    return c ? `${formatIp({ version: c.version, value: c.network })}/${String(c.prefix)}` : null;
}
/** Whether `ip` lies in `cidr` (both IPv4, or both IPv6; IPv4-mapped IPv6 counts as IPv4). */
export function ipInCidr(ip, cidr) {
    const address = parseIp(ip);
    const range = parseCidr(cidr);
    if (!range || address?.version !== range.version)
        return false;
    const bits = bitsOf(range.version);
    return (address.value >> BigInt(bits - range.prefix) === range.network >> BigInt(bits - range.prefix));
}
/**
 * FR-ATT-4: an office punch is inside when its fix is within any office's geofence
 * (accuracy-adjusted) or its IP is in any office's ranges. Pass active offices only. Location
 * is checked first, so the office reported is the one the person stands in.
 */
export function matchOffice(punch, offices) {
    if (punch.fix) {
        const fix = punch.fix;
        const office = offices.find((o) => insideGeofence(fix, o));
        if (office)
            return { inside: true, officeId: office.id, by: 'LOCATION' };
    }
    if (punch.ip) {
        const ip = punch.ip;
        const office = offices.find((o) => o.ipRanges.some((r) => ipInCidr(ip, r)));
        if (office)
            return { inside: true, officeId: office.id, by: 'IP' };
    }
    return { inside: false, officeId: null, by: null };
}
//# sourceMappingURL=geo.js.map